// "No goal" is a real, storable goal rather than the absence of one. It is what
// the picker offers so a user can clear a wrong goal — there is no other way to
// unset one — and what the bot webhook writes when the AI finds nothing in the
// org's catalog that fits a meeting.
//
// Because it is stored like any other goal, the nightly aggregation buckets it
// like any other goal and the Strategic Focus chart gets a slice for free.
//
// Mirrors NO_GOAL_LABEL / NO_GOAL_COLOR in
// gaintime-bot-webhook/schemas/strategicGoalSchema.js — the two repos have no
// shared package, so these strings have to agree by hand.
export const NO_GOAL_LABEL = "No goal";

// A Mantine colour *name*, not a hex: GoalSplitCard resolves it with
// `theme.colors[name][6]` and no optional chaining, so anything unknown throws.
export const NO_GOAL_COLOR = "gray";

// Meetings with no goal document at all: never had a bot, so nothing judged
// them, and never tagged by hand either. Unlike "No goal" this is not stored
// anywhere — gaintime-bgjobs/js/utils/strategicGoalBuckets.js invents it while
// aggregating, so that every meeting lands in exactly one slice of the chart.
export const OTHERS_LABEL = "Others";

// Neither of these is goal coverage. An org whose meetings are all untagged has
// nothing to show and should keep seeing the chart's empty state.
export const NON_GOAL_BUCKETS = [NO_GOAL_LABEL, OTHERS_LABEL];

export const READ_ONLY_NOTE =
  "Strategic goal can only be updated by meeting's organizer or admin if the organizer is internal";

/**
 * What the goal pill says on hover.
 *
 * For a meeting the AI declined to match, the stored reason is the whole point —
 * "No goal" on its own explains nothing. A goal the user picked by hand carries
 * no reason, so the label is shown instead, exactly as before.
 *
 * Returns `{ label, isSentence }`; `isSentence` tells the caller not to
 * lowercase the text, which is fine for a goal name and mangles a sentence.
 */
export const buildGoalTooltip = ({ goal, reason, canEdit }) => {
  const explanation = goal === NO_GOAL_LABEL ? (reason || "").trim() : "";
  const hoverText = explanation || goal;

  if (canEdit) {
    return {
      label: hoverText || "Click to set strategic goal",
      isSentence: Boolean(explanation),
    };
  }

  return {
    label: hoverText ? `${hoverText} - ${READ_ONLY_NOTE}` : READ_ONLY_NOTE,
    isSentence: Boolean(explanation),
  };
};
